import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../utils/auth';
import { getBackendUrl } from '../../utils/api';

// Ant Design imports
import {
  Card,
  Typography,
  Button,
  Alert,
  Space,
  Tag,
  Avatar,
  Divider,
  Skeleton,
  Row,
  Col,
  Tooltip,
  Flex,
} from 'antd';

// Ant Design Icons
import {
  ReloadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  DatabaseOutlined,
  CloudOutlined,
  CloudServerOutlined,
  LinkOutlined,
  ExportOutlined,
  ClusterOutlined,
} from '@ant-design/icons';

const { Title, Text, Link } = Typography;

interface ServiceStatusData {
  service: string;
  status: string;
  version: string;
  database?: string;
  redis?: string;
  qdrant?: string;
  workers?: number;
  broker?: string;
  url?: string;
  admin_url?: string | null;
  error?: string;
}

function ServiceStatus() {
  const [services, setServices] = useState<ServiceStatusData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const loadServiceStatus = useCallback(async () => {
    try {
      setLoading(true);

      const response = await adminFetch(`${getBackendUrl()}/api/admin/services/status`);

      if (response.ok) {
        const data = await response.json();
        const servicesData = data.data?.services || [];
        // Sort services: healthy first, then degraded, then unhealthy
        const sortedServices = servicesData.sort((a: ServiceStatusData, b: ServiceStatusData) => {
          const statusOrder: { [key: string]: number } = { healthy: 0, degraded: 1, unhealthy: 2, unknown: 3 };
          return (
            (statusOrder[a.status?.toLowerCase() || 'unknown'] || 99) -
            (statusOrder[b.status?.toLowerCase() || 'unknown'] || 99)
          );
        });
        setServices(sortedServices);
        setError('');
      } else if (response.status === 401) {
        setError('Authentication failed. Please check your token.');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        setError(`Failed to load service status: ${errorData.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      setError(`Error loading service status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServiceStatus();
    const interval = setInterval(loadServiceStatus, 10000);
    return () => clearInterval(interval);
  }, [loadServiceStatus]);

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'green';
      case 'degraded':
        return 'orange';
      case 'unhealthy':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return <CheckCircleOutlined />;
      case 'degraded':
        return <WarningOutlined />;
      case 'unhealthy':
        return <CloseCircleOutlined />;
      default:
        return <QuestionCircleOutlined />;
    }
  };

  const getConnectionColor = (status: string): string => {
    return status === 'connected' ? 'green' : 'red';
  };

  // Calculate summary stats
  const healthyCount = services.filter((s) => s.status === 'healthy').length;
  const degradedCount = services.filter((s) => s.status === 'degraded').length;
  const unhealthyCount = services.filter((s) => s.status === 'unhealthy').length;

  if (loading && services.length === 0) {
    return (
      <div>
        <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
          <Skeleton.Input active style={{ width: 200 }} />
          <Skeleton.Button active style={{ width: 100 }} />
        </Flex>
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </Col>
          ))}
        </Row>
        <Row gutter={[24, 24]}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Col xs={24} sm={12} md={8} key={i}>
              <Skeleton active paragraph={{ rows: 5 }} />
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Service Status
        </Title>
        <Tooltip title="Refresh service status to see the latest data">
          <Button icon={<ReloadOutlined />} onClick={loadServiceStatus} loading={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Tooltip>
      </Flex>

      {/* Error Alert */}
      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError('')}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      {/* Summary Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total Services
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700 }}>
                  {services.length}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#e6f4ff' }} icon={<ClusterOutlined style={{ color: '#1677ff' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Healthy
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#52c41a' }}>
                  {healthyCount}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#f6ffed' }} icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Degraded
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#faad14' }}>
                  {degradedCount}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#fffbe6' }} icon={<WarningOutlined style={{ color: '#faad14' }} />} />
            </Flex>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Flex justify="space-between" align="flex-start">
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Unhealthy
                </Text>
                <Title level={2} style={{ margin: 0, fontWeight: 700, color: '#ff4d4f' }}>
                  {unhealthyCount}
                </Title>
              </div>
              <Avatar style={{ backgroundColor: '#fff2f0' }} icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />} />
            </Flex>
          </Card>
        </Col>
      </Row>

      {/* Service Cards */}
      <Row gutter={[24, 24]}>
        {services.map((service) => (
          <Col xs={24} sm={12} md={8} key={service.service}>
            <Card
              hoverable
              style={{ height: '100%' }}
            >
              <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
                <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                  {service.service}
                </Title>
                <Tag color={getStatusColor(service.status)} icon={getStatusIcon(service.status)}>
                  {service.status}
                </Tag>
              </Flex>

              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Flex justify="space-between">
                  <Text type="secondary">Version</Text>
                  <Text strong>{service.version || 'N/A'}</Text>
                </Flex>

                {service.database && (
                  <Flex justify="space-between" align="center">
                    <Space size={4}>
                      <DatabaseOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary">Database</Text>
                    </Space>
                    <Tag color={getConnectionColor(service.database)} style={{ margin: 0 }}>
                      {service.database}
                    </Tag>
                  </Flex>
                )}

                {service.redis && (
                  <Flex justify="space-between" align="center">
                    <Space size={4}>
                      <CloudOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary">Redis</Text>
                    </Space>
                    <Tag color={getConnectionColor(service.redis)} style={{ margin: 0 }}>
                      {service.redis}
                    </Tag>
                  </Flex>
                )}

                {service.qdrant && (
                  <Flex justify="space-between" align="center">
                    <Space size={4}>
                      <CloudOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary">Qdrant</Text>
                    </Space>
                    <Tag color={getConnectionColor(service.qdrant)} style={{ margin: 0 }}>
                      {service.qdrant}
                    </Tag>
                  </Flex>
                )}

                {service.workers !== undefined && (
                  <Flex justify="space-between" align="center">
                    <Space size={4}>
                      <CloudServerOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary">Workers</Text>
                    </Space>
                    <Text strong>{service.workers}</Text>
                  </Flex>
                )}

                {service.broker && (
                  <Flex justify="space-between" align="center">
                    <Text type="secondary">Broker</Text>
                    <Tooltip title={service.broker}>
                      <Text
                        style={{
                          maxWidth: 180,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 12,
                        }}
                      >
                        {service.broker}
                      </Text>
                    </Tooltip>
                  </Flex>
                )}

                {service.url && (
                  <Flex justify="space-between" align="center">
                    <Space size={4}>
                      <LinkOutlined style={{ color: '#8c8c8c' }} />
                      <Text type="secondary">URL</Text>
                    </Space>
                    <Tooltip title={service.url}>
                      <Link
                        href={service.url}
                        target="_blank"
                        style={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          fontSize: 12,
                        }}
                      >
                        {service.url}
                      </Link>
                    </Tooltip>
                  </Flex>
                )}

                {service.admin_url && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Link
                      href={service.admin_url}
                      target="_blank"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        padding: '8px 0',
                        fontWeight: 500,
                      }}
                    >
                      Open Dashboard
                      <ExportOutlined />
                    </Link>
                  </>
                )}

                {service.error && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Alert message={service.error} type="error" style={{ padding: '4px 8px' }} />
                  </>
                )}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {services.length === 0 && !loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Text type="secondary">No service status available</Text>
          </div>
        </Card>
      )}
    </div>
  );
}

export default ServiceStatus;
